import axios from 'axios';

import { API_STATUS,STATUS_CODES } from '@frontend/js/constants';
import {
    resetApiErrors,
    showErrorToast,
    hideLoadingIndicators,
} from '@frontend/js/common';
import { appConfig } from '@frontend/js/utils/appConfig';

let currentBlockUI = null;

const apiInstance = axios.create({
    baseURL: appConfig.appUrl,
});

apiInstance.interceptors.request.use(
    function (config) {
        const options = {
            showToast: config?.showToast ?? true,
            resetErrorsBeforeRequest: config?.resetErrorsBeforeRequest ?? true,
            blockUI: null,
        };

        try {
            if (currentBlockUI) {
                currentBlockUI.release();
                currentBlockUI.destroy();
            }

            if (config?.blockEl) {
                options.blockUI = new KTBlockUI(
                    config.blockEl,
                    config?.blockOptions
                );
                options.blockUI.block();

                currentBlockUI = options.blockUI;
            }

            if (options?.resetErrorsBeforeRequest) {
                resetApiErrors();
            }

            // Attach options to the config for use in the response interceptor
            config.options = options;

            return config;
        } catch (error) {
            showErrorToast('Something unexpected happened.');
            return Promise.reject(error);
        }
    },
    function (error) {
        try {
            showErrorToast('Something unexpected happened.');
            unBlockUi();
            hideLoadingIndicators();
        } catch (errorData) {
            return Promise.reject(errorData);
        }
    }
);

apiInstance.interceptors.response.use(
    function (response) {
        const { data, config } = response;
        const { options } = config;

        if (
            (data?.status === API_STATUS.SUCCESS || data?.status === true || data.status == STATUS_CODES.OK) &&
            data?.message
        ) {
            if (data?.message_type == 'warn') {
                showWarnToast(options, data.message);
            } else {
                showSuccessToast(options, data.message);
            }
        }

        options.removeDisabledState = config?.removeDisabledState !== false;

        unBlockUi(options);

        return data;
    },
    function (error) {
        const { config } = error || {};
        const { options } = config || {};

        const errorMessage = error?.response?.data?.message;

        if (error?.response?.status === 401) {
            window.location.href = '/login';
        }

        if (errorMessage) {
            if (errorMessage.includes('CSRF token mismatch')) {
                showErrorToast(
                    'Your session has expired. Redirecting you to login.'
                );

                let lastVisitedUrl = window.location.href;
                if (lastVisitedUrl.startsWith(window.location.origin) && !lastVisitedUrl.includes('/login')) {
                    localStorage.setItem('last_visited_url', lastVisitedUrl);
                }

                setTimeout(() => {
                    location = '/login';
                }, 1000);
            } else if (options?.showToast !== false) {
                showErrorToast(errorMessage);
            }
        } else {
            showErrorToast('Something unexpected happened.');
        }

        unBlockUi(options);

        return Promise.reject(error);
    }
);

/**
 * Function to unblock the ui.
 *
 * @param {object} options
 */
function unBlockUi(options) {
    if (options?.blockUI?.blocked) {
        options?.blockUI?.release();
        options?.blockUI?.destroy();
        currentBlockUI = null;
    }

    hideLoadingIndicators(options?.removeDisabledState);
}

/**
 * Function to show success toast.
 *
 * @param {object} options
 * @param {string} message
 */
function showSuccessToast(options,message) {
    if (options?.showToast) {
        toastr.success(message);
    }
}

/**
 * Function to show warn toast.
 *
 * @param {object} options
 * @param {string} message
 */
function showWarnToast(options,message) {
    if (options?.showToast) {
        toastr.warning(message);
    }
}

export default apiInstance;
